import { Injectable } from '@angular/core';
import { CustomHttpClientService } from '../infrastructure/services/custom-http-client.service';
import { BehaviorSubject, lastValueFrom, map } from 'rxjs';
import { InstructionDto } from '../modules/contact-registry/models/InstructionDto';
import { ErrorMessageDto } from '../models/ErrorMessageDto';
import { TextMessageDto } from '../models/TextMessageDto';
import { SelectListItem } from '../modules/shared/models/SelectListItems';
import { StateDto } from '../modules/shared/models/StateDto';
import { FileService } from '../infrastructure/services/file.service';
import { FileResponse } from '../models/FileResponse';
import { ConditionalMenuDto } from '../models/conditionalMenuDto';
import { AlertEvent } from '../modules/common-components/alert/models/alert-event';
import { MsalConfigDto } from '../models/MsalConfigDto';
import { PageContentDto } from '../modules/portal-settings/models/PageContentDto';
import {
  InformationCollectionDto,
  InformationDto,
} from '../modules/portal-settings/models/InformationDtos';
import { ModuleConfigDto } from '../models/ModuleConfigDto';

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  errorMessageArr: ErrorMessageDto[] = [];
  textMessageArr: BehaviorSubject<TextMessageDto[]> = new BehaviorSubject(
    [] as TextMessageDto[],
  );
  moduleConfigs: BehaviorSubject<ModuleConfigDto[]> = new BehaviorSubject(
    [] as ModuleConfigDto[],
  );
  pageContents: BehaviorSubject<PageContentDto[]> = new BehaviorSubject(
    [] as PageContentDto[],
  );
  conditionalMenuItems: BehaviorSubject<ConditionalMenuDto> =
    new BehaviorSubject({} as ConditionalMenuDto);
  msalConfigs$: BehaviorSubject<MsalConfigDto> = new BehaviorSubject(
    {} as MsalConfigDto,
  );

  constructor(
    private readonly http: CustomHttpClientService,
    private readonly fileService: FileService,
  ) {}

  GetTermsOfUserPdf(): Promise<FileResponse> {
    return lastValueFrom(
      this.fileService.GetFileResponse('/api/general/pdf/terms-of-use'),
    );
  }

  async GetAchOperatorsAsync(): Promise<SelectListItem[]> {
    return lastValueFrom(
      this.http
        .get<SelectListItem[]>('api/general/achoperators')
        .pipe(map((data: SelectListItem[]) => data)),
    );
  }

  async GetEntityTypesAsync(): Promise<SelectListItem[]> {
    return lastValueFrom(
      this.http
        .get<SelectListItem[]>('api/general/entitytypes')
        .pipe(map((data: SelectListItem[]) => data)),
    );
  }

  async GetUSAStateCodesAsync(): Promise<StateDto[]> {
    return lastValueFrom(
      this.http
        .get<StateDto[]>('api/general/usa-states')
        .pipe(map((data: StateDto[]) => data)),
    );
  }

  async GetTextsAsync(keys: string): Promise<any[]> {
    return lastValueFrom(
      this.http
        .get<any[]>(`api/general/texts?keys=${keys}`)
        .pipe(map((data: any[]) => data)),
    );
  }

  async GetTextAsync(): Promise<TextMessageDto[]> {
    return lastValueFrom(
      this.http
        .get<TextMessageDto[]>(`/api/general/texts`)
        .pipe(map((data: TextMessageDto[]) => data)),
    );
  }

  GetAllTextMessage() {
    this.GetTextAsync().then((data: TextMessageDto[]) => {
      this.textMessageArr.next(data);
    });
  }

  GetTextMessage(key: string): string {
    const message = this.textMessageArr
      .getValue()
      .find(
        (x: TextMessageDto) => x.textKey.toLowerCase() === key.toLowerCase(),
      );
    return message ? message.textValue : '';
  }

  async GetConfigsAsync(keys: string): Promise<any[]> {
    return lastValueFrom(
      this.http
        .get<any[]>(`api/general/configs?keys=${keys}`)
        .pipe(map((data: any[]) => data)),
    );
  }

  GetAllModuleConfigs() {
    this.GetModuleConfigsAsync('').then((data: ModuleConfigDto[]) => {
      this.moduleConfigs.next(data);
    });
  }

  async GetModuleConfigsAsync(keys: string): Promise<any[]> {
    return lastValueFrom(
      this.http
        .get<any[]>(`api/general/module-configs?keys=${keys}`)
        .pipe(map((data: any[]) => data)),
    );
  }

  async GetInstructionsByModule(
    moduleName: string | null,
  ): Promise<InstructionDto> {
    return lastValueFrom(
      this.http
        .get<InstructionDto>(`api/instructions/upload/${moduleName}`)
        .pipe(map((data) => data)),
    );
  }
  async GetTimeZone() {
    return lastValueFrom(
      this.http.get(`api/general/timezones`).pipe(map((data: any) => data)),
    );
  }

  async GetErrorAsync(): Promise<ErrorMessageDto[]> {
    return lastValueFrom(
      this.http
        .get<ErrorMessageDto[]>(`/api/general/errormessages`)
        .pipe(map((data: ErrorMessageDto[]) => data)),
    );
  }

  async GetTimeoutSessionValue(): Promise<number> {
    return lastValueFrom(
      this.http
        .get<number>(`/api/general/get-session-timeout-value`)
        .pipe(map((data: number) => data)),
    );
  }

  GetAllMessage() {
    this.GetErrorAsync().then((data: ErrorMessageDto[]) => {
      this.errorMessageArr = data;
    });
  }

  GetErrorMessage(key: string, placeholderValue = ''): string {
    const err = this.errorMessageArr?.find(
      (x: ErrorMessageDto) => x.errorMessageKey === key,
    );
    if (placeholderValue && err?.errorMessageString) {
      return err.errorMessageString.replace('{0}', placeholderValue);
    }
    return err ? err.errorMessageString : '';
  }

  getConditionalMenu() {
    return lastValueFrom(
      this.http
        .get<ConditionalMenuDto>(`/api/fi/sidebar`)
        .pipe(map((data: ConditionalMenuDto) => data)),
    );
  }

  GetCancelAlert() {
    const title = this.GetErrorMessage('VerbiageCancelTitle');
    const msg = this.GetErrorMessage('VerbiageCancelMessage');
    const alertEvent: AlertEvent = {
      title: title,
      message: msg,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      showCancelButton: true,
    };
    return alertEvent;
  }

  GetMsalConfigs() {
    const msalInitialized = localStorage.getItem('msalIintialized');
    if (msalInitialized) {
      this.msalConfigs$.next(JSON.parse(msalInitialized!));
      return Promise.resolve();
    }

    return lastValueFrom(
      this.http
        .get<any>(`/api/config-api/msal-configs`)
        .pipe(map((data: MsalConfigDto) => this.msalConfigs$.next(data))),
    );
  }

  GetAllPageContents() {
    this.GetPageContents().then((data: PageContentDto[]) => {
      this.pageContents.next(data);
    });
  }

  async GetPageContents(): Promise<PageContentDto[]> {
    return lastValueFrom(
      this.http
        .get<PageContentDto[]>(`/api/general/page-content`)
        .pipe(map((data: PageContentDto[]) => data)),
    );
  }

  GetPageContent(key: string): string {
    const message = this.pageContents
      .getValue()
      .find(
        (x: PageContentDto) =>
          x.key.toLowerCase() === key.toLowerCase() && x.status === 'Active',
      );
    return message ? message.pageMessage : '';
  }

  async GetDocusignScriptUrl(): Promise<string> {
    return lastValueFrom(
      this.http
        .get('api/config-api/docusign-script-url')
        .pipe(map((data: any) => data.url)),
    );
  }

  async GetInformation(): Promise<InformationCollectionDto> {
    return lastValueFrom(
      this.http.get('api/general/information').pipe(map((data: any) => data)),
    );
  }

  async GetInformationByName(
    keys: string[],
    exactMatch?: boolean,
  ): Promise<InformationDto[]> {
    return lastValueFrom(
      this.http
        .get(
          `api/general/information/filtered?keys=${keys}&exactMatch=${exactMatch ?? true}`,
        )
        .pipe(map((data: any) => data)),
    );
  }

  getInformationContentDecoder(response: InformationDto[], key: string) {
    return decodeURIComponent(
      response.find((x) => x.name == key)?.content ?? '',
    )
      .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
      .replace(/&quot;/g, '"') // Replace quotes
      .replace(/<\/?p>/g, ''); // Remove <p> and </p> tags
  }
}
